/**
 * AISearchAssistant – Drawer-based AI chat overlay
 *
 * Uses SSE streaming from the ai-orchestrator service to provide a
 * conversational search experience. Messages are streamed token-by-token.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchStore } from '../store/searchStore';
import { ENTITY_ICONS } from '../types';
import type { AIChatMessage } from '../types';
import '../styles/global-search.css';

const AISearchAssistant: React.FC = () => {
  const {
    aiDrawerOpen,
    setAiDrawerOpen,
    chatMessages,
    chatSources,
    chatLoading,
    chatStreaming,
    chatError,
    streamChatMessage,
    abortChatStream,
    clearChat,
  } = useSearchStore();

  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const handleSend = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed || chatLoading || chatStreaming) return;
    setInput('');
    streamChatMessage(trimmed);
  }, [input, chatLoading, chatStreaming, streamChatMessage]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  if (!aiDrawerOpen) return null;

  return (
    <div className="gs-ai-overlay">
      <div className="gs-ai-backdrop" onClick={() => setAiDrawerOpen(false)} />
      <div className="gs-ai-drawer" role="dialog" aria-label="AI Search Assistant">
        {/* Header */}
        <div className="gs-ai-drawer__header">
          <div className="gs-ai-drawer__header-left">
            <span className="gs-ai-drawer__header-icon">✨</span>
            <h3 className="gs-ai-drawer__title">AI Search Assistant</h3>
          </div>
          <div className="gs-ai-drawer__header-actions">
            <button
              type="button"
              className="gs-ai-drawer__clear-btn"
              onClick={clearChat}
              title="Clear conversation"
            >
              🗑️
            </button>
            <button
              type="button"
              className="gs-ai-drawer__close-btn"
              onClick={() => setAiDrawerOpen(false)}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="gs-ai-drawer__messages">
          {chatMessages.length === 0 && (
            <div className="gs-ai-drawer__empty">
              <span className="gs-ai-drawer__empty-icon">🤖</span>
              <p>Ask me anything about your data — customers, transactions, accounts, and more.</p>
            </div>
          )}

          {chatMessages.map((msg: AIChatMessage, idx: number) => (
            <div
              key={idx}
              className={`gs-ai-drawer__msg gs-ai-drawer__msg--${msg.role}`}
            >
              <div className="gs-ai-drawer__msg-bubble">
                {msg.content || (chatStreaming && idx === chatMessages.length - 1 && (
                  <span className="gs-ai-drawer__typing">●●●</span>
                ))}
              </div>
            </div>
          ))}

          {chatError && (
            <div className="gs-ai-drawer__error">
              {chatError}
            </div>
          )}

          {/* Sources */}
          {chatSources.length > 0 && (
            <div className="gs-ai-drawer__sources">
              <div className="gs-ai-drawer__sources-title">Sources</div>
              {chatSources.map((src) => (
                <div key={src.id} className="gs-ai-drawer__source-item">
                  <span>{ENTITY_ICONS[src.entityType]}</span>
                  <span>{src.title}</span>
                </div>
              ))}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="gs-ai-drawer__input-area">
          {chatStreaming && (
            <button
              type="button"
              className="gs-ai-drawer__stop-btn"
              onClick={abortChatStream}
            >
              ⏹ Stop generating
            </button>
          )}
          <div className="gs-ai-drawer__input-row">
            <textarea
              className="gs-ai-drawer__textarea"
              placeholder="Ask a question…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={chatLoading}
            />
            <button
              type="button"
              className="gs-ai-drawer__send-btn"
              onClick={handleSend}
              disabled={!input.trim() || chatLoading || chatStreaming}
              aria-label="Send message"
            >
              ➤
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AISearchAssistant;
